import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import API from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import CaseCompare, { CompareMatch, DecideExtra } from '../components/CaseCompare';
import { formatIsoDate } from '../utils/formatDateValue';
import { enumLabel } from '../utils/enumLabel';

/**
 * /merge/:matchId — the compare view as a full page, and the only place a duplicate decision is
 * made. Reached from "Mark as duplicate of…" / "Merge another case into this one" on Case Detail,
 * from the Needs Review list on AI Data Collection, and from "Open as page" on the Case Detail dedup
 * drawer. The decision goes through the same PATCH /api/dedup/matches/:id as every pipeline match.
 * After deciding, it returns to location.state.returnTo when the caller set one (the Needs Review
 * list does), otherwise to the case the decision leaves live. Not behind the manual-merge flag: the
 * pipeline's own matches are reviewed here too.
 */

interface MatchRow extends CompareMatch {
  status: string;
  reviewedAt?: string | null;
  reviewedBy?: { firstName: string; lastName: string } | null;
  dismissReason?: string | null;
  case: { id: string; caseNumber: string; status: string; isPrimaryData?: boolean };
  matchedCase: { id: string; caseNumber: string; status: string; isPrimaryData?: boolean } | null;
}

const MergeCase: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo: string | undefined = (location.state as { returnTo?: string } | null)?.returnTo;
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState(false);

  const q = useQuery<MatchRow>({
    queryKey: ['match', matchId],
    queryFn: async () => (await API.get(`/api/dedup/match/${matchId}`)).data,
    enabled: !!matchId,
    retry: false,
  });

  const m = q.data;
  const canDecide = user?.role === 'ADMIN' || user?.role === 'APPROVER';
  const mode: 'merge' | 'review' = m?.source === 'MANUAL' ? 'merge' : 'review';

  const decide = async (status: 'CONFIRMED' | 'DISMISSED', extra?: DecideExtra) => {
    if (!m) return;
    setDeciding(true);
    try {
      const res = await API.patch(`/api/dedup/matches/${m.id}`, { status, ...(extra || {}) });
      const d = res.data || {};
      queryClient.invalidateQueries({ queryKey: ['case', m.case.id] });
      if (m.matchedCase) queryClient.invalidateQueries({ queryKey: ['case', m.matchedCase.id] });
      queryClient.invalidateQueries({ queryKey: ['match', m.id] });
      queryClient.invalidateQueries({ queryKey: ['dedup-pending-matches'] });
      queryClient.invalidateQueries({ queryKey: ['dedup-archived'] });
      if (status === 'CONFIRMED') {
        const fields = (Array.isArray(d.fieldsApplied) ? d.fieldsApplied.length : 0) + (Array.isArray(d.blockFieldsApplied) ? d.blockFieldsApplied.length : 0) + (Array.isArray(d.victimFieldsApplied) ? d.victimFieldsApplied.length : 0);
        const c = d.copied || {};
        const parts = [
          fields ? `${fields} field${fields === 1 ? '' : 's'} set` : '',
          c.victims ? `${c.victims} victim${c.victims === 1 ? '' : 's'} brought over` : '',
          c.perpetrators ? `${c.perpetrators} perpetrator${c.perpetrators === 1 ? '' : 's'} linked` : '',
          c.violationDetails ? `${c.violationDetails} violation block${c.violationDetails === 1 ? '' : 's'} brought over` : '',
          c.developmentProjects ? `${c.developmentProjects} project${c.developmentProjects === 1 ? '' : 's'} copied` : '',
          c.documents ? `${c.documents} document${c.documents === 1 ? '' : 's'} moved` : '',
          c.comments ? `${c.comments} comment${c.comments === 1 ? '' : 's'} copied` : '',
          c.sources ? `${c.sources} source${c.sources === 1 ? '' : 's'} attached` : '',
        ].filter(Boolean);
        showToast(d.enriched === false ? 'error' : 'success',
          `Merged — ${m.case.caseNumber} archived into ${m.matchedCase?.caseNumber ?? 'the original'}${parts.length ? `; ${parts.join(', ')}` : ''}${d.enriched === false ? '; the remaining additions could not be proposed (validator unreachable)' : ''}`);
        navigate(returnTo ?? (m.matchedCase ? `/cases/${m.matchedCase.id}` : `/cases/${m.case.id}`));
      } else {
        if (extra?.dismissReason === 'SAME_SITUATION') showToast('success', 'Dismissed as the same situation');
        else showToast('success', m.source === 'MANUAL' ? 'Not related — recorded; nothing else changes' : 'Dismissed — case sent to validation');
        navigate(returnTo ?? `/cases/${m.case.id}`);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to update match');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-start gap-3 mb-4">
        <button onClick={() => (returnTo ? navigate(returnTo) : navigate(-1))} aria-label="Back" className="mt-0.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{mode === 'merge' ? 'Merge two write-ups of one event' : 'Is this the same incident?'}</h1>
          {m && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              <Link to={`/cases/${m.case.id}`} className="font-medium text-primary hover:underline">{m.case.caseNumber}</Link>
              <span className="mx-1.5 text-gray-400">{mode === 'merge' ? '(duplicate) into' : 'may duplicate'}</span>
              {m.matchedCase ? <Link to={`/cases/${m.matchedCase.id}`} className="font-semibold text-gray-900 dark:text-gray-100 hover:underline">{m.matchedCase.caseNumber}</Link> : 'an unknown case'}
              {mode === 'merge' && <span className="ml-1.5 text-gray-400">(original)</span>}
            </p>
          )}
        </div>
      </div>

      {q.isLoading && <div className="py-12 text-center text-gray-400 dark:text-gray-500">Loading…</div>}
      {q.isError && <div className="py-12 text-center text-sm text-red-700 dark:text-red-300">This match could not be loaded. It may have been removed, or the merge tools are off on the server.</div>}

      {m && m.status !== 'PENDING_REVIEW' && (
        <div className="mb-4 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-4 py-3 text-sm text-gray-800 dark:text-gray-100">
          This pair was already decided: <span className="font-medium">{enumLabel(m.status)}</span>
          {m.reviewedAt && <> on {formatIsoDate(m.reviewedAt)}</>}
          {m.reviewedBy && <> by {m.reviewedBy.firstName} {m.reviewedBy.lastName}</>}
          {m.dismissReason && <> ({enumLabel(m.dismissReason).toLowerCase()})</>}.
          {m.status === 'CONFIRMED' && <> Un-merge from the archived case's banner if that was wrong.</>}
        </div>
      )}

      {m && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" style={{ minHeight: '60vh' }}>
          <CaseCompare match={m} onDecide={decide} deciding={deciding} mode={mode} canDecide={canDecide && m.status === 'PENDING_REVIEW'} />
        </div>
      )}
    </div>
  );
};

export default MergeCase;
